import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { exportFichaDistribuidorPdf } from './exportFichaDistribuidorPdf';
import DistribuidorFichaCard from './DistribuidorFichaCard';
import DistribuidorGraficaCard from './DistribuidorGraficaCard';

/**
 * Componente principal para clientes catalogados como distribuidores.
 * Integra:
 * 1. Ficha de Identificación del Distribuidor (diseño réplica de Imagen 2 con datos reales de Akasia y botón de descarga).
 * 2. Card de Métricas y Gráfica Mensual (diseño réplica de Imagen 1 TimeMetricCard con acento morado y líneas de referencia).
 */
const DistribuidorEstadisticasCard = ({ idClienteAkasia, customerdetails = {} }) => {
    const { token } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const [cargando, setCargando] = useState(true);
    const [mesesRango, setMesesRango] = useState(6); // 3 o 6 meses
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [resumen, setResumen] = useState({
        dTotalMesActual: 0,
        dPromedioUltimos3Meses: 0,
        dPromedio3MesesAnteriores: 0,
        iOrdenesMesActual: 0,
        iOrdenesMesAnterior: 0,
        iOrdenesMesAntepasado: 0
    });
    const [historico, setHistorico] = useState([]);
    const [descargandoPdf, setDescargandoPdf] = useState(false);

    const formatLocalDateStr = (dt) => {
        if (!dt) return '';
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const d = String(dt.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const cargarEstadisticas = async (fechaObj = selectedDate) => {
        if (!idClienteAkasia) return;

        setCargando(true);
        try {
            const fechaStr = formatLocalDateStr(fechaObj);
            const response = await fetch(
                `/api/distribuidor/GetEstadisticasOrdenes?idClienteAkasia=${idClienteAkasia}&dFecha=${fechaStr}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                const tableResumen = data.table2 || data.Table2 || [];
                const tableHistorico = data.table3 || data.Table3 || [];

                if (tableResumen.length > 0) {
                    setResumen(tableResumen[0]);
                }
                setHistorico(tableHistorico);
            } else {
                showErrorToast('No se pudieron cargar las estadísticas del distribuidor');
            }
        } catch (error) {
            console.error('Error al cargar estadísticas del distribuidor:', error);
            showErrorToast('Error de conexión al cargar estadísticas');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargarEstadisticas(selectedDate);
    }, [idClienteAkasia, selectedDate]);

    const handleDescargarPdf = () => {
        try {
            setDescargandoPdf(true);
            exportFichaDistribuidorPdf({
                distribuidor: {
                    idClienteAkasia,
                    ...customerdetails
                },
                estadisticas: {
                    resumen,
                    historico
                }
            });
            showSuccessToast('Ficha de identificación descargada exitosamente');
        } catch (error) {
            console.error('Error al generar PDF:', error);
            showErrorToast('Error al generar la ficha en PDF');
        } finally {
            setDescargandoPdf(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3 mb-3">
            {/* 1. FICHA DE IDENTIFICACIÓN DEL DISTRIBUIDOR (Diseño réplica Imagen 2) */}
            <DistribuidorFichaCard
                distribuidor={{
                    idClienteAkasia,
                    ...customerdetails
                }}
                onDescargarPdf={handleDescargarPdf}
                descargandoPdf={descargandoPdf}
            />

            {/* 2. CARD DE MÉTRICAS Y GRÁFICA DE ÓRDENES (Diseño réplica Imagen 1 TimeMetricCard) */}
            <DistribuidorGraficaCard
                resumen={resumen}
                historico={historico}
                cargando={cargando}
                mesesRango={mesesRango}
                setMesesRango={setMesesRango}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
            />
        </div>
    );
};

DistribuidorEstadisticasCard.propTypes = {
    idClienteAkasia: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    customerdetails: PropTypes.object
};

export default DistribuidorEstadisticasCard;
